import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatBoxQty, formatProductName } from './zovcurInvoiceStore';

const NAVY = [14, 61, 82];
const TEAL = [14, 138, 154];
const SLATE = [91, 107, 124];
const INK = [30, 41, 59];
const LIGHT = [244, 247, 250];
const WHITE = [255, 255, 255];
const BORDER = [226, 232, 240];
const ROSE = [159, 18, 57];
const AMBER = [180, 83, 9];
const EMERALD = [4, 120, 87];

const money = (value) =>
  `Rs ${(Number(value) || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtNum = (value) => (Number(value) || 0).toLocaleString('en-IN');

const drawStatCard = (doc, x, y, w, h, title, value, sub = '', tone = NAVY) => {
  doc.setDrawColor(...BORDER);
  doc.setFillColor(...LIGHT);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, 2, 2, 'FD');
  doc.setFillColor(...tone);
  doc.rect(x, y, 1.6, h, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...SLATE);
  doc.text(String(title).toUpperCase(), x + 4, y + 5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...tone);
  doc.text(String(value), x + 4, y + 12);
  if (sub) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...SLATE);
    const lines = doc.splitTextToSize(String(sub), w - 8);
    doc.text(lines, x + 4, y + 17);
  }
};

/**
 * Build Stock In / Out report PDF (landscape) with stats + full stock table.
 */
export const buildStockInOutPdf = ({
  rows = [],
  stats = {},
  filters = {},
  generatedAt = new Date()
}) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 10;
  let y = 10;

  // Header bar
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 22, 'F');
  doc.setFillColor(...TEAL);
  doc.rect(0, 22, pageW, 1.5, 'F');

  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Zovcur Pharma', margin, 10);
  doc.setFontSize(11);
  doc.text('Stock In Report', margin, 17);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(186, 230, 235);
  doc.text(`Generated: ${fmtDate(generatedAt)} ${generatedAt.toLocaleTimeString('en-IN')}`, pageW - margin, 10, {
    align: 'right'
  });
  doc.text(`${rows.length} product record${rows.length === 1 ? '' : 's'}`, pageW - margin, 16, {
    align: 'right'
  });

  y = 28;

  // Active filters
  const filterParts = [];
  if (filters.search) filterParts.push(`Search: ${filters.search}`);
  if (filters.product && filters.product !== 'all') filterParts.push(`Product: ${filters.product}`);
  if (filters.manufacturer && filters.manufacturer !== 'all') {
    filterParts.push(`Manufacturer: ${filters.manufacturer}`);
  }
  if (filters.unit && filters.unit !== 'all') filterParts.push(`Unit: ${filters.unit}`);
  if (filters.balance && filters.balance !== 'all') filterParts.push(`Balance: ${filters.balance}`);
  if (filters.dateFrom) filterParts.push(`From: ${filters.dateFrom}`);
  if (filters.dateTo) filterParts.push(`To: ${filters.dateTo}`);

  if (filterParts.length) {
    doc.setFillColor(236, 253, 245);
    doc.roundedRect(margin, y, pageW - margin * 2, 8, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...TEAL);
    doc.text(`Filters · ${filterParts.join('  |  ')}`, margin + 3, y + 5.2);
    y += 11;
  }

  // Inventory stats
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text('INVENTORY SUMMARY', margin, y + 3);
  y += 6;

  const cardW = (pageW - margin * 2 - 12) / 5;
  const cardH = 22;
  const invCards = [
    { title: 'Products', value: String(stats.products || 0), sub: 'On this report', tone: NAVY },
    {
      title: 'Stock In',
      value: `${formatBoxQty(stats.stockBoxes || 0)} box`,
      sub: `${fmtNum(stats.stockStrips)} strips`,
      tone: TEAL
    },
    {
      title: 'Ordered (billed)',
      value: `${formatBoxQty(stats.orderedBoxes || 0)} box`,
      sub: `${fmtNum(stats.orderedStrips)} strips`,
      tone: AMBER
    },
    {
      title: 'Sample',
      value: `${formatBoxQty(stats.sampleBoxes || 0)} box`,
      sub: `${fmtNum(stats.sampleStrips)} strips`,
      tone: [109, 40, 217]
    },
    {
      title: 'Balance / Low',
      value: `${formatBoxQty(stats.balanceBoxes || 0)} box`,
      sub: `${stats.lowStock || 0} low stock`,
      tone: EMERALD
    }
  ];
  invCards.forEach((card, i) => {
    drawStatCard(doc, margin + i * (cardW + 3), y, cardW, cardH, card.title, card.value, card.sub, card.tone);
  });
  y += cardH + 5;

  // Cost stats
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text('COST SUMMARY', margin, y + 3);
  y += 6;

  const costW = (pageW - margin * 2 - 9) / 4;
  const costCards = [
    {
      title: 'Total Cost',
      value: money(stats.totalCost),
      sub: 'Stock strips × doctor price',
      tone: NAVY
    },
    {
      title: 'Invoice Cost',
      value: money(stats.invoiceCost),
      sub: `Base ${money(stats.baseCost)} + GST`,
      tone: AMBER
    },
    {
      title: 'Pending Amount',
      value: money(stats.pendingAmount),
      sub: `${stats.pendingCount || 0} unpaid / partial`,
      tone: ROSE
    },
    {
      title: 'Received Cost',
      value: money(stats.receivedCost),
      sub: 'Collected payments',
      tone: EMERALD
    }
  ];
  costCards.forEach((card, i) => {
    drawStatCard(doc, margin + i * (costW + 3), y, costW, cardH, card.title, card.value, card.sub, card.tone);
  });
  y += cardH + 6;

  // Stock table
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text('STOCK DETAILS', margin, y + 2);
  y += 4;

  const tableRows = rows.map((row) => [
    row.sno || '—',
    row.date || '—',
    row.product || '—',
    row.manufacturer || '—',
    row.batch || '—',
    row.exp || '—',
    row.stockIn || '—',
    row.orders || '—',
    row.free || '—',
    row.sample || '0',
    row.strips || '0',
    row.balance || '—'
  ]);

  autoTable(doc, {
    startY: y,
    head: [
      [
        'SNO',
        'Date',
        'Product',
        'Manufacturer',
        'Batch',
        'Exp',
        'Stock In',
        'Orders',
        'Free',
        'Sample',
        'Strips',
        'Balance'
      ]
    ],
    body: tableRows.length
      ? tableRows
      : [['—', '—', 'No stock records', '—', '—', '—', '—', '—', '—', '—', '—', '—']],
    theme: 'grid',
    styles: {
      fontSize: 7,
      cellPadding: 1.6,
      textColor: INK,
      lineColor: BORDER,
      lineWidth: 0.2,
      overflow: 'linebreak',
      valign: 'middle'
    },
    headStyles: {
      fillColor: NAVY,
      textColor: WHITE,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'left'
    },
    alternateRowStyles: { fillColor: LIGHT },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center' },
      1: { cellWidth: 20 },
      2: { cellWidth: 36 },
      3: { cellWidth: 28 },
      4: { cellWidth: 18 },
      5: { cellWidth: 18 },
      6: { cellWidth: 18 },
      7: { cellWidth: 28 },
      8: { cellWidth: 24 },
      9: { cellWidth: 14, halign: 'center' },
      10: { cellWidth: 16, halign: 'right' },
      11: { cellWidth: 22 }
    },
    margin: { left: margin, right: margin, bottom: 14 }
  });

  // Footer on all pages
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFillColor(...NAVY);
    doc.rect(0, pageH - 9, pageW, 9, 'F');
    doc.setFillColor(...TEAL);
    doc.rect(0, pageH - 9, pageW, 1.1, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(186, 230, 235);
    doc.text('Zovcur Pharma · Stock In report', margin, pageH - 3.5);
    doc.text(`Page ${i} of ${pageCount}`, pageW - margin, pageH - 3.5, { align: 'right' });
  }

  return doc;
};

export const generateStockInOutPdf = (payload) => {
  const doc = buildStockInOutPdf(payload);
  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`stock-in-report-${stamp}.pdf`);
  return doc;
};

/** Map UI row data for the PDF table. */
export const buildStockInOutPdfRows = ({
  transactions = [],
  summaryByTransaction = {},
  getDisplaySno,
  formatDate,
  getStockBatchExp
}) =>
  transactions.map((transaction) => {
    const summary = summaryByTransaction[transaction._id] || {};
    const stockBatchExp = getStockBatchExp?.(transaction) || { batch: '—', expiry: null };
    const freeCost = Number(summary.freeCost) || 0;
    return {
      sno: getDisplaySno?.(transaction) || transaction.sno || '—',
      date: formatDate?.(transaction.date) || '—',
      product: formatProductName(transaction.materialName) || '—',
      manufacturer: transaction.manufacturer || '—',
      batch: stockBatchExp.batch || '—',
      exp: formatDate?.(stockBatchExp.expiry) || '—',
      stockIn: `${transaction.quantity || 0} ${transaction.unit || ''}`.trim(),
      orders: `${summary.totalBilledStrips || 0} strips / ${formatBoxQty(summary.totalBilledBoxes || 0)} box`,
      free:
        (summary.totalFreeStrips || 0) > 0
          ? `${summary.totalFreeStrips || 0} strips${
              freeCost > 0 ? ` / Rs ${freeCost.toLocaleString('en-IN')}` : ''
            }`
          : '—',
      sample: String(summary.totalSamples || 0),
      strips: fmtNum(summary.balanceStrips),
      balance: `${formatBoxQty(summary.balance || 0)} ${transaction.unit || 'Box'}`
    };
  });
